// Gear that is built for one age standing next to gear that is not
// (PLAN-04 S6). Three separate mistakes live here, and they are not the same
// mistake:
//
// 1. **A chick at an adult feeder.** A chick at a trough built for a hen
//    feathers the feed, walks in it, and fouls it. There is no published rate
//    for any of that, so the penalty is a design value — but it is a real one,
//    and it is the reason a brooder gets its own small gear rather than the
//    yard's.
//
// 2. **A layer at a chick fount.** INC-16's own spec puts six 1-quart jars on
//    every 100 chicks in the first week and two 2-gallon waterers from one to
//    four, which is what says a fount is built for chicks. A hen at one empties
//    it far faster than it was rated for.
//
// 3. **The pebble hack.** A deep open drinker is a hazard to a chick of 0-3
//    weeks: in Normal she soaks and chills, which feeds the wet-and-cold
//    physiology the game already has, and in Veteran she can drown. Pebbles in
//    the water, or a drinker actually built for chicks, removes it. This one is
//    Punto Pio's own practice (Field Notes J11, owner-confirmed 2026-09-26),
//    not a research finding — INC-17 looked specifically and found no source
//    stating the hazard or the fix, which is why the fact card stays
//    unverified and the odds here are design values.
//
// Deterministic like the rest of src/sim: every roll comes off a stream keyed
// to the bird and the hour.

import { clamp } from '../core/math';
import type { ChickenEntity, GameState, Structure } from '../core/types';
import { hiddenStatMultiplier } from './access';
import { getConfig, type BiologyConfig } from './config';
import { effectsOf, getItem } from './items';
import { rollStream } from './league';
import { addCondition } from './disease';

// --- is this gear the right age? --------------------------------------------

/** How a piece of gear sits against the bird using it. */
export type GearFit = 'right' | 'too_big' | 'too_small';

/**
 * What a piece of gear is to a bird of this age.
 *
 * The rating lives on the item (`ageRange`) because that is what a catalog
 * states; the comparison is here because that is what the sim asks. Gear with
 * no rating is right for everybody — a dust bath is not the wrong size for a
 * chick.
 */
export function gearFitFor(chicken: ChickenEntity, itemId: string): GearFit {
  const range = getItem(itemId)?.ageRange;
  if (!range) return 'right';
  const weeks = chicken.ageInDays / 7;
  if (weeks < range.minWeeks) return 'too_big';
  if (range.maxWeeks !== undefined && weeks > range.maxWeeks) return 'too_small';
  return 'right';
}

/** How much of the flock's thirst this drinker is rated for, in birds. */
export function ratedBirds(config: BiologyConfig = getConfig('normal')): number {
  return config.gear.chickFountRatedBirds;
}

/**
 * How much faster a fount empties than it was rated to, and how many birds it
 * takes to empty it. A layer drinks about four times what a chick does
 * (NUT-10: 180-250 ml against 30-60), so four of them is the work of nearly
 * twenty chicks — which is what a fount is rated for.
 */
export function fountOverage(
  state: GameState,
  feeder: Structure,
  config: BiologyConfig = getConfig('normal'),
): number {
  if (!getItem(feeder.itemId)?.ageRange) return 0;
  const chickNeed = config.intake.waterMlPerDay.chick;
  const equivalents = state.flock
    .filter((c) => c.alive)
    .reduce((sum, c) => sum + config.intake.waterMlPerDay[stageKey(c)] / chickNeed, 0);
  const rated = ratedBirds(config);
  return Math.max(0, equivalents - rated);
}

function stageKey(chicken: ChickenEntity): 'chick' | 'pullet' | 'adult' {
  if (chicken.stage === 'pullet') return 'pullet';
  return chicken.stage === 'adult' ? 'adult' : 'chick';
}

/** The drain multiplier for a drinker, 1 when the birds using it fit. */
export function fountDrainMultiplier(
  state: GameState,
  feeder: Structure,
  config: BiologyConfig = getConfig('normal'),
): number {
  const over = fountOverage(state, feeder, config);
  if (over <= 0) return 1;
  const rated = ratedBirds(config);
  return 1 + config.gear.overCapacityDrainMultiplier * (over / rated);
}

// --- the deep waterer --------------------------------------------------------

/** True when the player owns the pebble hack (Field Notes J11). */
export function hasPebbles(state: GameState, config: BiologyConfig = getConfig('normal')): boolean {
  return (state.inventory[config.gear.pebbleItemId] ?? 0) > 0;
}

/** True when a waterer is built for chicks rather than for the yard. */
export function isChickWaterer(feeder: Structure): boolean {
  return Boolean(effectsOf(feeder).chickSafe);
}

/**
 * Why a chick is safe at this drinker, if she is. `null` means she is not.
 *
 * Two answers, and only two: pebbles in the water, or a drinker actually built
 * for a chick. Everything else is a deep open container with a baby in it.
 */
export function chickWaterSafety(
  state: GameState,
  feeder: Structure,
  config: BiologyConfig = getConfig('normal'),
): 'pebbles' | 'chick_fount' | null {
  if (isChickWaterer(feeder)) return 'chick_fount';
  if (hasPebbles(state, config)) return 'pebbles';
  return null;
}

/** A chick this young can still fall into an open drinker (design window). */
export function isDeepWatererVulnerable(
  chicken: ChickenEntity,
  config: BiologyConfig = getConfig('normal'),
): boolean {
  return chicken.stage === 'chick' && chicken.ageInDays / 7 <= config.gear.deepWatererWeeks;
}

/** The drinkers a chick would actually drink from, and whether they are safe. */
export function unsafeWaterersFor(
  state: GameState,
  chicken: ChickenEntity,
  config: BiologyConfig = getConfig('normal'),
): Structure[] {
  if (!isDeepWatererVulnerable(chicken, config)) return [];
  return state.structures.filter(
    (s) =>
      effectsOf(s).providesWater &&
      (s.state.water ?? 0) > 5 &&
      chickWaterSafety(state, s, config) === null,
  );
}

// --- the step ----------------------------------------------------------------

export interface GearStepReport {
  /** Chicks that fell in and are wet and cold. */
  soaked: string[];
  /** Chicks that drowned. Veteran only. */
  drowned: string[];
}

/**
 * The whole of S6 in one step: the wrong-size hygiene penalty, the fount that
 * empties too fast, and the deep waterer.
 *
 * `hours` is passed rather than assumed, and the odds are per hour, so a live
 * tick and a quarter-hour catch-up step charge the same rate.
 */
export function stepGear(
  state: GameState,
  hours: number,
  config: BiologyConfig,
  sickNeverDie: boolean,
): GearStepReport {
  const report: GearStepReport = { soaked: [], drowned: [] };
  const hour = Math.floor(state.simHour);

  // Chicks at a trough built for hens foul it, and the feed carries it into
  // them. The trough's own dirtiness is the visible half.
  const feeders = state.structures;
  const chicksAtWrongGear = new Map<Structure, number>();
  for (const chicken of state.flock) {
    if (!chicken.alive || chicken.stage !== 'chick') continue;
    for (const feeder of feeders) {
      const effects = effectsOf(feeder);
      const uses =
        (effects.providesFeed && (feeder.state.feed ?? 0) > 5) ||
        (effects.providesWater && (feeder.state.water ?? 0) > 5);
      if (!uses) continue;
      if (gearFitFor(chicken, feeder.itemId) !== 'too_big') continue;
      chicksAtWrongGear.set(feeder, (chicksAtWrongGear.get(feeder) ?? 0) + 1);
    }
  }
  for (const [feeder, count] of chicksAtWrongGear) {
    feeder.state.dirtiness = clamp(
      (feeder.state.dirtiness ?? 0) + config.gear.chickAtAdultFeederDirtPerHour * count * hours,
      0,
      100,
    );
    const damage = config.gear.chickAtAdultFeederGutPerHour * count * hours;
    for (const chicken of state.flock) {
      if (!chicken.alive || chicken.stage !== 'chick') continue;
      if (gearFitFor(chicken, feeder.itemId) !== 'too_big') continue;
      chicken.physiology.gutHealth = clamp(chicken.physiology.gutHealth - damage, 0, 100);
    }
  }

  // The deep waterer, rolled per bird per hour off its own stream so it cannot
  // be re-drawn by how much else the sim did first.
  for (const chicken of state.flock) {
    if (!chicken.alive) continue;
    if (unsafeWaterersFor(state, chicken, config).length === 0) continue;
    const roll = rollStream(state, `soak:${chicken.id}:${hour}`);
    // A hardy chick is a less likely one to end up in it. This is the first
    // thing in the game to read a hidden stat (PLAN-04 S4).
    const relief = hiddenStatMultiplier(chicken, 'hardiness') - 1;
    const soak = clamp(
      config.gear.soakChancePerHour * (1 - config.gear.hardinessSoakRelief * (relief * 10)),
      0,
      1,
    );
    if (roll.chance(soak * hours)) {
      if (addCondition(chicken, 'wet_and_cold', state.simHour, 'mild')) {
        report.soaked.push(chicken.id);
      }
      // Veteran can lose her. Normal never does, whatever else it does.
      if (!sickNeverDie && roll.chance(config.gear.drownChancePerSoak)) {
        chicken.alive = false;
        chicken.deathCause = 'drowning';
        chicken.diedOnDay = Math.floor(state.simHour / 24);
        report.drowned.push(chicken.id);
        continue;
      }
    }
  }
  return report;
}
